import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useOrganizationLinkSave } from "@/queries/use-organization-links.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiOrganizationLink,
  ApiOrganizationLinkPayload,
} from "@/types/organization-link.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface OrganizationLinkEditModalProps extends ModalProps {
  organizationLink: ApiOrganizationLink | null;
}

export function OrganizationLinkEditModal({
  organizationLink,
  ...props
}: OrganizationLinkEditModalProps) {
  const { id } = { ...props };
  const { t } = useTranslation();
  const { close } = useModals();

  const schemaObject = z.object({
    title: z.string().trim().min(1, t("errors:field_required")),
    url: z.string().trim().min(1, t("errors:field_required")).url(t("errors:field_url_invalid")),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ApiOrganizationLinkPayload>({
    mode: "onSubmit",
    resolver: zodResolver(schemaObject),
    defaultValues: {
      title: organizationLink?.title ?? "",
      url: organizationLink?.url ?? "",
    },
  });

  const organizationLinkSave = useOrganizationLinkSave(control);

  const onSubmit = handleSubmit(async (data) => {
    const payload: ApiOrganizationLinkPayload = {
      title: data.title.trim(),
      url: data.url.trim(),
    };

    try {
      const saved = await organizationLinkSave.mutateAsync({
        id: organizationLink?.id,
        payload: payload,
      });
      if (organizationLink) {
        analyticsService.trackEvent(analyticEvents.organizationLinks.edited, {
          [analyticProperties.id]: organizationLink.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.organizationLinks.created, {
          [analyticProperties.id]: saved.id,
        });
      }
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:organization_link")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={organizationLinkSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            label={t("common:title")}
            required={true}
            type={"text"}
            autoFocus={true}
            {...register("title")}
            error={errors.title?.message}
          />
          <Input
            label={t("common:url")}
            required={true}
            type={"url"}
            {...register("url")}
            error={errors.url?.message}
          />
        </Stack>
      </form>
    </Modal>
  );
}
